import {
	SET_WIDGETS,
	CREATE_WIDGET,
	UPSERT_PEER_WIDGET,
	UPDATE_WIDGET,
	DELETE_WIDGET,
	CLEAR_WIDGETS,
} from '../actions/actionTypes';
import { tryParse } from 'utils';

const preState = [];
const draftWidgetPrefix = 'draft-widget';

const mapGroupIDs = groups => groups.map(el => (el.id ? el.id : el));
const hasServerWidgetId = widget =>
	widget && widget.id !== undefined && widget.id !== null && widget.id !== 0;

const normalizeWidget = widget => {
	const props = tryParse(widget.props) || {};
	const groupIDs = mapGroupIDs(widget.groups || []);
	return { ...widget, props, groups: groupIDs };
};

const mergeWidget = (existing, incoming) => {
	const normalized = normalizeWidget(incoming);
	const groups =
		incoming.groups !== undefined && incoming.groups !== null
			? normalized.groups
			: existing.groups || [];
	return { ...existing, ...normalized, groups, toDelete: false, toUpdate: false };
};

export const createDraftWidgetId = () => {
	return `${draftWidgetPrefix}-${Date.now()}-${Math.random()
		.toString(36)
		.slice(2, 10)}`;
};

export const getWidgetKey = widget => {
	if (!widget) return null;
	if (hasServerWidgetId(widget)) return `server-${widget.id}`;
	if (widget.clientId) return `draft-${widget.clientId}`;
	return null;
};

export const createDraftWidget = widget => ({
	...widget,
	clientId: widget.clientId || createDraftWidgetId(),
});

export const getVisibleWidgets = widgets => widgets.filter(widget => widget && !widget.toDelete);

export const mergeWidgetsWithDeleted = (visibleWidgets, widgets) => [
	...visibleWidgets,
	...widgets.filter(widget => widget && widget.toDelete),
];

export const widgetsReducer = (state = preState, action) => {
	const stateDup = [...state];
	const { type, widgets, widget, widgetID, clientId } = action;
	switch (type) {
		case SET_WIDGETS:
			return [...widgets.filter(widget => widget.id !== 0).map(normalizeWidget)];
		case CLEAR_WIDGETS:
			return [];
		// Keyed by clientId, not the echoed index — that's sender-local, and matching it leaked pages.
		case CREATE_WIDGET: {
			if (!clientId) return state;
			const draftIndex = state.findIndex(w => w?.clientId === clientId);
			if (draftIndex === -1) return state;
			return state.map((w, i) => (i === draftIndex ? mergeWidget(w, widget) : w));
		}
		// Adopt it: it's already in this page's widget_order, so omitting it drops it on our next save.
		case UPSERT_PEER_WIDGET: {
			if (!hasServerWidgetId(widget)) return state;
			if (state.some(w => w?.id === widget.id)) return state;
			return mergeWidgetsWithDeleted([...getVisibleWidgets(state), normalizeWidget(widget)], state);
		}
		case UPDATE_WIDGET:
			const { id } = widget;
			return stateDup.map(w => (w.id === id ? mergeWidget(w, widget) : w));
		case DELETE_WIDGET:
			const removedList = stateDup.filter(w => widgetID !== w.id);
			return removedList;
		default:
			return state;
	}
};

export default widgetsReducer;
